import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { MprcButton, MprcSpinnerIndicator } from 'components';
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { IAdvFilterMenuItem } from "views/general/AdvancedFilter/interfaces";

////////////////////////////////////////////////////////////////////////////

interface IProps<TOrderBy> {
    items: Array<IAdvFilterMenuItem<TOrderBy>>;
    onChange: (selectedValue: IAdvFilterMenuItem<TOrderBy>) => void;
    textOrIcon: React.ReactNode;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
    title?: string;
    color?: MprcColors | "inherit";
    onGetData?: () => Promise<boolean>;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Componente dropdown do filtro avançado.
 * @param props
 */
export const AdvancedFilterDropdown = <TOrderBy extends unknown>(props: IProps<TOrderBy>) => {

    const { title, textOrIcon, items, disabled, onChange, onGetData, color = "inherit", size = "small" } = props;

    const [loading, setLoading] = useState<boolean>(false);
    const [anchor, setAnchor] = useState<Element | null>(null);

    const menuItems = useMemo(() => {
        var nodes: React.ReactNode[] = [];

        if (loading)
            nodes.push(<Box sx={{ mt: 1.5, ml: 1, width: 100 }} key="loading"><MprcSpinnerIndicator /></Box>);

        items?.forEach((v, index, arr) => {
            if (index > 0 && arr[index].group != arr[index - 1].group) {
                nodes.push(<Box sx={{ mt: 1.5 }} key={`${index}_divider`} />);
            }
            nodes.push(
                <MenuItem
                    key={index}
                    disabled={v.disabled}
                    sx={{ lineHeight: "14px" }}
                    onClick={
                        () => {
                            setAnchor(null);
                            onChange(v);
                        }
                    }
                >
                    {
                        v.checked ? (
                            <React.Fragment>
                                <b>
                                    <FontAwesomeIcon fixedWidth icon={faCheck} />
                                </b>
                            </React.Fragment>
                        ) : (
                            <div className="fa fa-check fa-w-16 fa-fw"></div>
                        )
                    }
                    {v.text}
                </MenuItem>
            );
        });
        return nodes;
    }, [items, loading]);

    useEffect(() => {
        if (loading && onGetData) {
            onGetData().finally(() => setLoading(false));
        }
    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {
                typeof (textOrIcon) == "string"
                    ?
                    <MprcButton
                        title={title}
                        text={textOrIcon}
                        color={color}
                        size={size}
                        onClick={(e) => {
                            if (items?.length == 0 && onGetData) setLoading(true);
                            setAnchor(e.currentTarget);
                        }}
                        endIcon={<KeyboardArrowDownIcon />}
                    />
                    :
                    <IconButton
                        title={title}
                        color={color}
                        size={size}
                        disabled={disabled}
                        onClick={(e) => {
                            if (items?.length == 0 && onGetData) setLoading(true);
                            setAnchor(e.currentTarget);
                        }}
                    >
                        {textOrIcon}
                    </IconButton>
            }
            <Menu
                anchorEl={anchor}
                open={anchor != null}
                onClose={
                    () => {
                        setAnchor(null);
                    }
                }
            >
                {menuItems}
            </Menu>
        </>
    );
};
